import { Component, Element, Event, EventEmitter, Method, Prop, h, Host, Watch, State } from '@stencil/core';
import { focusElement, generateId } from '../../utils/functions';

/**
 * Radio buttons are used to capture a user choice within a set of related options.
 */
/**
 * @slot label - Label for the radio button. Alternatively, you can use the label attribute.
 */
@Component({
  tag: 'som-radio-button',
  styleUrl: 'som-radio-button.scss',
})
export class SomRadioButton {
  @Element() el: HTMLElement;

  /**
   * This property is the radio button label text.
   */
  @Prop() label: string = '';

  /**
   * This property is a list of CSS classes that can be used to style the label.  
   * @deprecated with version 4.0.1 release
   */
   @Prop() labelClass?: string = '';    
   
   /**
   * This property will visually hide the label.  A label is still required to be provided for screenreader support.
   */
  @Prop() hideLabel: boolean = false;

  /**
   * This property can be used to set the ARIA label associated with this input (for screen readers).
   */
  @Prop() inputAriaLabel: string | null = null;

  /**
   * This property is the form element name (used when submitting the form).
   */
  @Prop() name: string = '';

  /**
   * This property is used to set the radio button as "checked".
   */
  @Prop({ mutable: true, reflect: true }) checked: boolean = false;

  /**
   * This property is the radio button value sent on submit when using it in a form.
   */
  @Prop() value: string = '';

  /**
   * This boolean is used to set the radio button as disabled.
   */
  @Prop() disabled: boolean = false;

  /**
   * This boolean can be used to set the input as required.
   */
   @Prop() required: boolean = false;

  /**
   * This property can be used to specifically set an ID to the input field. If an ID is not provided, it will be dynamically generated.
   */
  @Prop() inputId: string = generateId('som-radio-button');

  /**
   * This event is emitted when when the radio button gets focus.
   */
  @Event() somFocus: EventEmitter;

  /**
   * This event is emitted when when the radio button loses focus.
   */
  @Event() somBlur: EventEmitter;

  /**
   * This event is emitted when the radio button is toggled.
   */
  @Event() somChange: EventEmitter;

  inputElement: HTMLInputElement;

  handleFocusCb;

  handleBlurCb;

  handleChangeCb;

  radioButtonEl;

  @State() groupHelpId: string = '';

  @Watch('group-help-id')
  handleGroupHelpIdChange(newVal) {
    this.groupHelpId = newVal;
  }

  /**
   * This public method sets focus to the radio element.
   * @returns Promise will resolve after focus.
   */
  @Method()
  setFocus(): Promise<void> {
    return focusElement(this.inputElement);
  }

  handleFocus(e) {
    this.somFocus.emit(e);
  }

  handleBlur(e) {
    this.somBlur.emit(e);
  }

  handleChange(e) {
    this.checked = e.target.checked;
    this.somChange.emit(e);
  }

  registerEvents() {
    this.inputElement.addEventListener('focus', this.handleFocusCb);
    this.inputElement.addEventListener('blur', this.handleBlurCb);
    this.inputElement.addEventListener('change', this.handleChangeCb);
  }

  unregisterEvents() {
    this.inputElement.removeEventListener('focus', this.handleFocusCb);
    this.inputElement.removeEventListener('blur', this.handleBlurCb);
    this.inputElement.removeEventListener('change', this.handleChangeCb);
  }

  componentWillLoad() {
    this.groupHelpId = this.el.getAttribute('group-help-id');
    
    this.handleFocusCb = this.handleFocus.bind(this);
    this.handleBlurCb = this.handleBlur.bind(this);
    this.handleChangeCb = this.handleChange.bind(this);
  }

  componentDidLoad() {
    this.inputElement = this.el.querySelector(`#${this.inputId}`);
    this.registerEvents();
  }

  componentWillUnload() {
    this.unregisterEvents();
  }

  render() {
    // Removing aria label on 'som-radio-button' since we set it to the <input /> element.
    const ariaDescribedby = (this.groupHelpId) ? {'aria-describedby' : this.groupHelpId} : null;
    return (
      <Host aria-label={null}>
        <div class="custom-control custom-radio">
          <input
            id={this.inputId}
            ref={ (el) => this.radioButtonEl = el }
            class="custom-control-input"
            type="radio"
            name={this.name}
            value={this.value}
            disabled={this.disabled}
            checked={this.checked}
            required={this.required}
            aria-required={this.required}
            aria-label={this.inputAriaLabel}
            {...ariaDescribedby}
          />
          
          <label htmlFor={this.inputId} class={`custom-control-label ${this.labelClass} ${this.hideLabel ? 'sr-only' : ''}`}>
            <slot name="label">{this.label}</slot>
          </label>
        </div>
      </Host>
    );
  }
}
